import { Paper } from "@mui/material";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  setQueryOutput,
  setOutputMetadata,
  setComparisonQueryOutput,
  setIsPerformanceApiCalled,
  setOverviewAccountSummaryActiveRequestKey,
  setOverviewAccountSummaryLoadedRequestKey,
  setOverviewAccountSummaryOutput,
  setIsFetchedBudgetData,
  setBudgetDataOfBizDevelopment,
  setBudgetMetaDataOfBizDevelopment,
  setIsBudgetOverused,
  setBeAwareFraudPlayersOutput,
  setBeAwareFraudPlayersLoadedRequestKey,
  setBeAwareNegativeBalanceOutput,
  setBeAwareNegativeBalanceLoadedRequestKey,
  setBeAwareConversionsUploadOutput,
  setBeAwareConversionsUploadLoadedRequestKey,
} from "../../../../features/bizDevelopmentDashboardSlice";
import BizDevelopmentDashboardSearch from "./BizDevelopmentDashboardSearch";
import BizDevelopmentDashboardTabs from "./BizDevelopmentDashboardTabs";
import { useBizDevelopmentDashboard } from "../../../../pages/BizDevelopment/Dashboard/hooks/useBizDevelopmentDashboard";
import { resetbizDevelopmentAnomaliesState } from "../../../../features/bizDevelopmentAnomaliesSlice";

const BizDevelopmentDashboard = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();
  const [dataFetched, setDataFetched] = useState(
    () => !!searchParams.get("sites"),
  );
  const {
    fetchBudgetData,
    getSitesFromParams,
    getOverviewAccountSummaryTimeframeFromParams,
    fetchOverviewAccountSummaryData,
    fetchBeAwareFraudPlayersData,
    fetchBeAwareNegativeBalanceData,
    fetchBeAwareConversionsUploadData,
    fetchMissingData,
    fetchFtdData,
    fetchAnomaliesBrandData,
    fetchAnomaliesIncomeData,
    fetchAnomaliesEarningsData,
  } = useBizDevelopmentDashboard();

  // On refresh, re-trigger API if params are already in the URL
  useEffect(() => {
    const sites = getSitesFromParams();
    if (sites.length === 0) return;
    const activeTab = searchParams.get("tab") || "performance";
    // "performance" and "be_aware" self-manage their initial fetch on mount
    if (activeTab !== "performance" && activeTab !== "be_aware") {
      triggerApiForTab(activeTab, sites);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const triggerApiForTab = (activeTab: string, siteSharedUids: string[]) => {
    switch (activeTab) {
      case "performance":
        // Performance.tsx useEffect handles both main + comparison fetch
        // when isPerformanceApiCalled transitions to false (set in handleGetData)
        break;
      case "overview":
        fetchOverviewAccountSummaryData(
          getOverviewAccountSummaryTimeframeFromParams(),
          siteSharedUids,
        );
        break;
      case "budget_control":
        fetchBudgetData(siteSharedUids);
        break;
      case "anomalies":
        const anomaliesTab = searchParams.get("anomaliesTab") || "missing_data";
        if (anomaliesTab === "ftd") {
          fetchFtdData(siteSharedUids);
        } else if (anomaliesTab === "missing_data") {
          fetchMissingData(siteSharedUids);
        } else if (anomaliesTab === "brands") {
          fetchAnomaliesBrandData(siteSharedUids);
        } else if (anomaliesTab === "income") {
          fetchAnomaliesIncomeData(siteSharedUids);
        } else if (anomaliesTab === "earnings") {
          fetchAnomaliesEarningsData(siteSharedUids);
        }
        break;

      case "be_aware":
        // Refetch all three Be Aware tables for the newly selected sites.
        fetchBeAwareFraudPlayersData(siteSharedUids);
        fetchBeAwareNegativeBalanceData(siteSharedUids);
        fetchBeAwareConversionsUploadData(siteSharedUids);
        break;
      // future cases for other tabs with their own APIs can be added here
      // future tabs will add their own API calls here
      default:
        break;
    }
  };

  // Reset every Be Aware subtab's cached output and request key so stale data
  // for a previous website selection is never shown.
  const clearBeAwareData = () => {
    dispatch(setBeAwareFraudPlayersOutput([]));
    dispatch(setBeAwareFraudPlayersLoadedRequestKey(null));
    dispatch(setBeAwareNegativeBalanceOutput([]));
    dispatch(setBeAwareNegativeBalanceLoadedRequestKey(null));
    dispatch(setBeAwareConversionsUploadOutput([]));
    dispatch(setBeAwareConversionsUploadLoadedRequestKey(null));
  };

  const handleGetData = (siteSharedUids: string[]) => {
    setDataFetched(true);
    const activeTab = searchParams.get("tab") || "performance";
    dispatch(setIsPerformanceApiCalled(false));
    dispatch(setIsFetchedBudgetData(false)); // reset budget data fetched state when fetching new data

    dispatch(resetbizDevelopmentAnomaliesState()); //clear anomalies data in store
    triggerApiForTab(activeTab, siteSharedUids);
  };

  const handleClearSites = () => {
    setDataFetched(false);
    setSearchParams(new URLSearchParams());
    dispatch(setQueryOutput([]));
    dispatch(setOutputMetadata({}));
    dispatch(setIsPerformanceApiCalled(false));
    dispatch(setComparisonQueryOutput([]));
    dispatch(setOverviewAccountSummaryOutput([]));
    dispatch(setOverviewAccountSummaryLoadedRequestKey(null));
    dispatch(setOverviewAccountSummaryActiveRequestKey(null));

    //clear budget data in store
    dispatch(setIsFetchedBudgetData(false));
    dispatch(setBudgetDataOfBizDevelopment([]));
    dispatch(setBudgetMetaDataOfBizDevelopment({}));
    dispatch(setIsBudgetOverused(false));

    //clear anomalies data in store
    dispatch(resetbizDevelopmentAnomaliesState());

    //clear Be Aware data in store
    clearBeAwareData();
  };

  return (
    <>
      <Paper>
        <BizDevelopmentDashboardSearch
          onGetData={handleGetData}
          onClearSites={handleClearSites}
        />
        {dataFetched && <BizDevelopmentDashboardTabs />}
      </Paper>
    </>
  );
};

export default BizDevelopmentDashboard;
